import nav from '@/containers/_nav_admin'
import {
  canManageProviderNoticesAndHolidays,
  canManageProviderRoles,
  canOpenProviderBilling,
  canOpenTenantBilling,
  canOpenProviderAccessManagement,
  canOpenTenantAccessManagement,
  filterNavigation
} from './accessPolicy'

/**
 * 左メニューを組み立てる。**「役割・会社の機能・権限」の組を渡すと、その人の左メニューを返す。**
 *
 * 左メニューの部品（TheAdminSidebar）の中に書いていたものを、そのまま切り出した（2026-09-26）。
 * 権限管理の画面で「この権限グループで使えるメニュー」を出すとき、**左メニューと同じ判定で
 * 計算するため。**画面ごとに一覧を手で書くと、左メニューを直したときに一覧だけ古くなる。
 *
 * @param context { roles: [], features: [], permissions: [] }
 */
export function buildSidebarNavigation (context) {
  const draft = JSON.parse(JSON.stringify(nav))
  const root = draft[0]
  if (root && Array.isArray(root._children)) {
    // **運営（PREIKA側）のものと、買った会社のものを分ける。**
    // ひとつの「権限管理」にまとめていたころは、運営にしか出ない行と
    // 会社の管理者に出る行が同じ見出しの下に並んでいて、
    // 自分が触ってよいものかが見出しから読めなかった。
    const 運営のもの = []
    const 自社のもの = []

    if (canOpenProviderAccessManagement(context)) {
      運営のもの.push({
        _name: 'CSidebarNavItem',
        name: '契約企業一覧',
        to: '/platform/access-management',
        icon: 'cil-building'
      })
    }
    if (canManageProviderRoles(context)) {
      運営のもの.push({
        _name: 'CSidebarNavItem',
        name: '運営権限管理',
        to: '/platform/provider-role-management',
        icon: 'cil-shield-alt'
      })
    }
    /*
     * **お知らせ配信と祝日マスタは、運営管理者にも出す**（2026-09-30、束7）。
     * 前は運営権限管理と同じ「緊急最高管理者だけ」の中にあり、運営管理者は
     * 年に1回の祝日の取り込みのたびに緊急用の役割で入り直していた。
     */
    if (canManageProviderNoticesAndHolidays(context)) {
      /*
       * **経路を作っただけでは、誰もたどり着けない。**お知らせは
       * 出す人がいて初めて出るので、入口が無いと「出せる仕組みは
       * あるが1件も出ない」で終わる。
       */
      運営のもの.push({
        _name: 'CSidebarNavItem',
        name: 'お知らせ配信',
        to: '/platform/notices',
        icon: 'cil-bell'
      })
      /*
       * **在庫が切れたことに気づける場所。**祝日マスタは
       * 読む側しか無く、何年先まで入っているかを誰も見ていな
       * かった。入口をここへ置いて、開けば残りが見えるようにする。
       */
      運営のもの.push({
        _name: 'CSidebarNavItem',
        name: '祝日マスタ',
        to: '/platform/national-holidays',
        icon: 'cil-calendar'
      })
    }
    /*
     * **請求は、権限を分けて出す。**
     *
     * 参照（PROVIDER_BILLING_VIEW）はサポートにも開く。金額と
     * 入金が見えないと「払ったのに止まった」に答えられない。
     * 発行（PROVIDER_INVOICE_ISSUE）は画面の中でボタンを分ける。
     */
    if (canOpenProviderBilling(context)) {
      運営のもの.push({
        _name: 'CSidebarNavItem',
        name: '請求管理',
        to: '/platform/billing',
        icon: 'cil-money'
      })
      /*
       * **未収を追う場所を、メニューに出す。**
       *
       * 請求管理はその月だけを見る画面なので、古い月のぶんが
       * 残ったまま新しい月だけ払われている状態は見つけられない。
       * 請求管理の中のタブにすると、また「場所が分からない」に
       * なるので、入口を分けて出す（2026-09-18、利用者の指摘）。
       */
      運営のもの.push({
        _name: 'CSidebarNavItem',
        name: '入金・売掛管理',
        to: '/platform/receivables',
        /*
         * **icons.js に登録された名前だけを書く。**
         * 無い名前を書くと画面に「undefined メニュー名」と出る
         * （2026-09-18、CI が捕まえた）。cil-chart-line は未登録。
         */
        icon: 'cil-wallet'
      })
    }
    if (canOpenTenantAccessManagement(context)) {
      自社のもの.push({
        _name: 'CSidebarNavItem',
        name: '権限管理',
        to: '/admin/tenant-access-management',
        icon: 'cil-people'
      })
      /*
       * **端末を失くした社員を救う道。**
       *
       * ここに出さないと、画面はあるのに URL を直打ちするしか
       * 開けない。2026-08-22 まで「2要素を失った人を誰も元に
       * 戻せない」状態だったのを直したのに、その入口が無い。
       *
       * 出す条件は権限管理と同じ。back も
       * /tenant-access-management を TENANT_OWNER と
       * TENANT_SYSTEM_ADMIN だけに絞っている
       * （ApiAccessPolicyService）。
       */
      自社のもの.push({
        _name: 'CSidebarNavItem',
        name: '2要素認証リセット',
        to: '/admin/mfa-recovery',
        icon: 'cil-lock-unlocked'
      })
    }
    /*
     * **いちばん下へ足す。**
     *
     * ダッシュボードの直下（上から2番目）へ入れていたころは、
     * 会社の管理者にとって<b>いちばん触らないものが最初に来て</b>、
     * 毎月出す勤務表や経費精算が下へ押し下げられていた。
     * 権限や契約は一度決めたらしばらく触らない。
     */
    /*
     * **ご請求は「自社の権限」とは別の見出しへ。**
     * 権限の話ではないので、同じ見出しの下に並べると
     * 自分が触ってよいものかが見出しから読めない。
     */
    const 自社の請求 = []
    if (canOpenTenantBilling(context)) {
      自社の請求.push({
        _name: 'CSidebarNavItem',
        name: '請求・お支払い',
        to: '/admin/billing',
        icon: 'cil-wallet'
      })
    }
    if (自社のもの.length) {
      root._children.push(
        { _name: 'CSidebarNavTitle', _children: ['自社の権限'] },
        ...自社のもの
      )
    }
    if (自社の請求.length) {
      root._children.push(
        { _name: 'CSidebarNavTitle', _children: ['ご請求'] },
        ...自社の請求
      )
    }
    if (運営のもの.length) {
      root._children.push(
        { _name: 'CSidebarNavTitle', _children: ['PREIKA運営'] },
        ...運営のもの
      )
    }
  }
  return filterNavigation(draft, context)
}

/**
 * 左メニューに出る項目を、見出しつきの平らな一覧にする。
 * @returns [{ group: '月次報告', name: '勤務表', to: '/report/roster' }, ...]
 */
export function sidebarMenuItems (context) {
  const root = buildSidebarNavigation(context)[0]
  const items = []
  let group = ''
  ;((root && root._children) || []).forEach(item => {
    if (!item) return
    if (item._name === 'CSidebarNavTitle') {
      group = Array.isArray(item._children) ? String(item._children[0] || '') : ''
      return
    }
    if (item._name === 'CSidebarNavItem' && item.to) items.push({ group, name: item.name, to: item.to })
  })
  return items
}
